// Apple-style Bundles Section Animations
// Smooth, elegant scroll-triggered animations

class BundlesAnimations {
  constructor() {
    this.section = null;
    this.isMobile = this.checkMobile();
    this.init();
  }

  checkMobile() {
    return window.innerWidth <= 749;
  }

  init() {
    this.section = document.querySelector('.bundles-section');
    if (!this.section) return;

    // Wait for GSAP and ScrollTrigger
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(() => this.init(), 50);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    // Initialize all animations (same on mobile and desktop)
    this.animateWave();
    this.animateHeader();
    this.animateCard();
    this.animateBadge();
    this.animateCardImage();
  }

  initMobileAnimations() {
    // Simple fade-in for mobile
    const elements = [
      this.section.querySelector('.bundles-section__header'),
      this.section.querySelector('.bundle-promo-card'),
    ];

    elements.forEach(el => {
      if (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      }
    });

    const badge = this.section.querySelector('.bundle-promo-card__badge');
    if (badge) {
      badge.style.opacity = '1';
      badge.style.transform = 'none';
    }

    // Show floating leaves on mobile with simple CSS animation (very slow and gentle)
    const shapes = this.section.querySelectorAll('.shape');
    const line = this.section.querySelector('.divider-line');
    
    shapes.forEach((shape, index) => {
      shape.style.opacity = '0.7';
      shape.style.animation = `mobileFloat ${12 + index * 2}s ease-in-out infinite alternate`;
      shape.style.animationDelay = `${index * 1}s`;
    });

    if (line) {
      line.style.opacity = '0.6';
    }

    // Add mobile keyframes if not already added
    if (!document.getElementById('mobile-bundles-keyframes')) {
      const style = document.createElement('style');
      style.id = 'mobile-bundles-keyframes';
      style.textContent = `
        @keyframes mobileFloat {
          0% { transform: translateY(0) rotate(0deg); }
          100% { transform: translateY(-8px) rotate(3deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  animateWave() {
    const divider = this.section.querySelector('.bundles-section__divider');
    if (!divider) return;

    const shapes = divider.querySelectorAll('.shape');
    const line = divider.querySelector('.divider-line');

    // Animate the divider line reveal
    if (line) {
      gsap.to(line, {
        opacity: 0.8,
        duration: 1.5,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: divider,
          start: 'top 90%',
          toggleActions: 'play none none reverse',
        },
      });

      // Pulse animation on the line
      gsap.to(line, {
        opacity: 0.4,
        duration: 2,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    }

    // Animate each hemp leaf with elegant floating
    shapes.forEach((shape, index) => {
      // Staggered animation properties for each leaf
      const duration = 4 + index * 0.5;
      const delay = index * 0.3;
      const yOffset = -30 - index * 5;
      const xOffset = (index - 1) * 15; // Spread them out horizontally
      const rotation = (index - 1) * 20; // Different rotation for each

      // Initial reveal with fade and scale
      gsap.to(shape, {
        opacity: 0.8,
        scale: 1,
        duration: 1,
        delay: delay,
        ease: 'back.out(1.2)',
        scrollTrigger: {
          trigger: divider,
          start: 'top 90%',
          toggleActions: 'play none none reverse',
        },
      });

      // Gentle floating animation (up and drift)
      gsap.to(shape, {
        y: yOffset,
        x: xOffset,
        duration: duration,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: delay,
      });

      // Slow rotation like leaves drifting
      gsap.to(shape, {
        rotation: rotation,
        duration: duration * 1.2,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: delay + 0.2,
      });

      // Gentle scale breathing effect
      gsap.to(shape, {
        scale: 1.15,
        duration: 3 + index * 0.3,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: delay + 0.5,
      });

      // Subtle opacity fade for dreamy effect
      gsap.to(shape, {
        opacity: 0.4,
        duration: 3.5 + index * 0.4,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: delay + 0.3,
      });
    });
  }

  animateHeader() {
    const header = this.section.querySelector('.bundles-section__header');
    if (!header) return;

    // Apple-style fade and slide up
    gsap.to(header, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: header,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });

    // Animate heading and subheading separately for stagger effect
    const heading = header.querySelector('.bundles-section__heading');
    const subheading = header.querySelector('.bundles-section__subheading');

    if (heading) {
      gsap.fromTo(heading,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: heading,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }

    if (subheading) {
      gsap.fromTo(subheading,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          delay: 0.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: subheading,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }
  }

  animateCard() {
    const card = this.section.querySelector('.bundle-promo-card');
    if (!card) return;

    // Apple-style scale and fade
    gsap.to(card, {
      opacity: 1,
      scale: 1,
      y: 0,
      duration: 1.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: card,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });

    // Parallax effect on scroll
    gsap.to(card, {
      y: -30,
      ease: 'none',
      scrollTrigger: {
        trigger: card,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
      },
    });
  }

  animateBadge() {
    const badge = this.section.querySelector('.bundle-promo-card__badge');
    if (!badge) return;

    // Pop-in animation with bounce
    gsap.to(badge, {
      opacity: 1,
      scale: 1,
      duration: 0.6,
      delay: 0.5,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: badge,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });

    // Subtle floating animation
    gsap.to(badge, {
      y: -5,
      duration: 2,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
    });
  }

  animateCardImage() {
    const imageWrapper = this.section.querySelector('.bundle-promo-card__image-wrapper');
    const image = this.section.querySelector('.bundle-promo-card__image');
    if (!image) return;

    // Subtle parallax on the image
    gsap.to(image, {
      y: 20,
      scale: 1.05,
      ease: 'none',
      scrollTrigger: {
        trigger: imageWrapper,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1.5,
      },
    });
  }
}

// Initialize
function waitForGSAP(callback, maxAttempts = 50) {
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    callback();
  } else if (maxAttempts > 0) {
    setTimeout(() => waitForGSAP(callback, maxAttempts - 1), 100);
  }
}

function initBundlesAnimations() {
  const bundlesSection = document.querySelector('.bundles-section');
  if (bundlesSection) {
    new BundlesAnimations();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    waitForGSAP(initBundlesAnimations);
  });
} else {
  waitForGSAP(initBundlesAnimations);
}

